import { isNil } from "lodash";
import { createSelector } from "reselect";

import calendarTypes from "../../../types/calendarTypes";
import { preferencesSelectors } from "../../preferences";
import { isGregorianCalendarFromCalendar, wasOldStyleGregorianCalendarFromCalendar } from "./gregorianCalendar";

// ==========================================================
// Exported Default Functions - to be used outside "calendar"
// ==========================================================

// Update from Matt:
// We are moving the storage of dateOrder and thousandSeparator outside of the calendar
// and into preferences, so that it is set globally by the user and not tied to a file
// However, there are thousands of places in the code that reference it within the calendar
// So rather than modify all of that code, the getCalendar() selector will be modified to
// instead merge in these values from preferences and return the cached value every time
const getCalendar = createSelector(
  // inputs
  state => state.definitions.calendar,
  preferencesSelectors.getDateOrder,
  preferencesSelectors.getThousandsSeparator,
  state => state.definitions.overrideNowTimestamp,
  // calculate based on inputs
  (calendar, dateOrder, thousandsSeparator, overrideNowTimestamp) =>
    calendar.merge({ dateOrder, thousandsSeparator, overrideNowTimestamp })
);

const wasOldStyleGregorianCalendar = state => wasOldStyleGregorianCalendarFromCalendar(getCalendar(state));
const isGregorianCalendar = state => isGregorianCalendarFromCalendar(getCalendar(state));
const isLeapYear = (state, era, year) => isLeapYearFromCalendar(getCalendar(state).era, year);
const getFirstDayOfEra = state => getWeekdayIndexAtZeroFromCalendar(getCalendar(state));
const isRelativeCalendar = state => getCalendarIsRelativeFromCalendar(getCalendar(state));

export const getCalendarType = state => getCalendar(state).calendarType;

export default {
  getCalendar,
  getCalendarType,
  isGregorianCalendar,
  wasOldStyleGregorianCalendar,
  isRelativeCalendar,
  isLeapYear,
  getFirstDayOfEra
};

// ==============================================================
// Exported "Internal" Functions - to be used within calendar and
// date folders only. Consider them as "protected" interface
// ==============================================================

// Internal calendar exports: these selectors are exported, but should only be used internally within
// the calendar folder (including the date calculation sub-folder).
export const getDateFormatFromCalendar = calendar => calendar.dateFormat;
export const getTimeFormatFromCalendar = calendar => calendar.timeFormat;
export const getDateOrderFromCalendar = calendar => calendar.dateOrder;

export const getLeapCyclesFromCalendar = calendar => calendar.leapCycles;

export const getErasFromCalendar = calendar => calendar.eras;
export const getMonthsFromCalendar = calendar => calendar.months;
export const getMonthsInYearFromCalendar = calendar => calendar.months.size;
export const getWeekdaysFromCalendar = calendar => calendar.weekdays;
export const getSecondsInDayFromCalendar = calendar => calendar.hoursInDay * 60 * 60;
export const getHoursInDayFromCalendar = calendar => calendar.hoursInDay;
export const getWeekdayIndexAtZeroFromCalendar = calendar => calendar.weekdayIndexAtZero;
export const getCalendarTypeFromCalendar = calendar => calendar.calendarType;
export const isCalendarTypeRelative = calendarType => calendarType !== calendarTypes.CALENDAR_TYPE_DATE_TIME;
export const getCalendarIsRelativeFromCalendar = calendar => isCalendarTypeRelative(calendar.calendarType);

export const isOngoingAllowedForCalendar = calendar => {
  const isRelative = getCalendarIsRelativeFromCalendar(calendar);
  return !isRelative || !isNil(calendar.overrideNowTimestamp);
};

export const getZeroDateTimestampFromCalendar = calendar => calendar.zeroDateTimestamp;

export const getEraFromCalendar = (calendar, index) => calendar.eras.get(index);
export const getMonthFromCalendar = (calendar, month) => calendar.months.get(month - 1);
export const getWeekdayFromCalendar = (calendar, index) => calendar.weekdays.get(index);

const yearNumberIsLeap = (leapCycles, leapOffset, year) => {
  for (let c = leapCycles.size - 1; c >= 0; c--) {
    if (c % 2 === 0) {
      // even indexes are inclusion tests
      if (year % leapCycles.get(c) === leapOffset) {
        return true;
      }
    } else {
      // idd indexes are exclusion tests
      if (year % leapCycles.get(c) === leapOffset) {
        return false;
      }
    }
  }
  // If we are not mod 0 anything, then we are not a leap year!
  return false;
  // year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
};

export const isLeapYearFromCalendar = (calendar, era, year) => {
  const eraRecord = getEraFromCalendar(calendar, era);
  return eraRecord.hasLeapYears && yearNumberIsLeap(calendar.leapCycles, eraRecord.leapOffset, year);
};

export const getLeapDurationForMonth = (calendar, month) => getMonthFromCalendar(calendar, month).leapDuration;
export const getNormalDurationForMonth = (calendar, month) => getMonthFromCalendar(calendar, month).normalDuration;
export const getEraIsBackwards = (calendar, era) => getEraFromCalendar(calendar, era).isBackwards;
export const getEraDuration = (calendar, era) => getEraFromCalendar(calendar, era).duration;

export const getDaysInMonth = (calendar, era, year, month) =>
  isLeapYearFromCalendar(calendar, era, year)
    ? getLeapDurationForMonth(calendar, month)
    : getNormalDurationForMonth(calendar, month);

export { isGregorianCalendarFromCalendar, isGregorianMonthsFromCalendar } from "./gregorianCalendar";
